/**
 * Casa del Bacalao · ficha de producto v2
 *
 * <casa-llevas>: "Llevas 2 kg · $1,898" bajo el selector de cantidad.
 * Todo en enteros: el total en centavos (precio × cantidad) y el peso en gramos o mililitros
 * (presentación × cantidad), sin redondear el peso. El HTML inicial ya viene calculado por Liquid;
 * aquí solo se recalcula cuando cambia la cantidad o la presentación (Horizon re-renderiza los
 * atributos data-* al cambiar de variante).
 *
 * <casa-actualiza data-casa-id="…">: al cambiar de presentación, Horizon solo re-renderiza el
 * selector de variantes y cada componente se actualiza con el HTML nuevo que trae el evento
 * `productSelect`. Este envoltorio hace lo mismo para los bloques de la casa ("Llevas…").
 */

import { StandardEvents } from '@shopify/events';

/**
 * Da formato a centavos con el formato de dinero de la tienda (mismo contrato que el filtro
 * `money_without_trailing_zeros` de Shopify: sin ",00"/".00" cuando no hay centavos).
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  const key = match ? match[1] : 'amount';

  /**
   * @param {number} value
   * @param {number} decimals
   * @param {string} thousands
   * @param {string} decimal
   */
  const withDelimiters = (value, decimals, thousands, decimal) => {
    const fixed = (value / 100).toFixed(decimals);
    const [whole = '0', fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (!fraction || /^0+$/.test(fraction)) return grouped;
    return `${grouped}${decimal}${fraction}`;
  };

  let value;
  switch (key) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = withDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return match ? format.replace(placeholder, value) : value;
}

/**
 * Peso o volumen legible sin redondear: 2500 g → "2.5 kg", 750 g → "750 g", 1500 ml → "1.5 L".
 * @param {number} base - cantidad entera en g o ml
 * @param {string} unit - 'g' | 'ml'
 */
function formatQuantity(base, unit) {
  const big = unit === 'ml' ? 'L' : 'kg';
  if (base < 1000) return `${base} ${unit}`;
  const whole = Math.floor(base / 1000);
  const rest = String(base % 1000).padStart(3, '0').replace(/0+$/, '');
  return rest ? `${whole}.${rest} ${big}` : `${whole} ${big}`;
}

class CasaLlevas extends HTMLElement {
  static get observedAttributes() {
    return ['data-base', 'data-unidad', 'data-precio'];
  }

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(
      'quantity-selector:update',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (!detail || detail.cartLine) return;
        this.#render(Number(detail.quantity));
      },
      { signal }
    );

    const form = this.closest('product-form-component') || this.closest('.product-details') || document;
    form.addEventListener(
      'input',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.name === 'quantity') this.#render();
      },
      { signal }
    );
    form.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.name === 'quantity') this.#render();
      },
      { signal }
    );

    this.#render();
    this.#cargarCarrito();

    // El carrito cambió (Horizon avisa por evento; además, tras enviar el formulario se vuelve a leer)
    ['cart:update', 'cart:updated', 'cart:refresh'].forEach((nombre) =>
      document.addEventListener(nombre, () => this.#cargarCarrito(), { signal })
    );
    document.addEventListener(
      'submit',
      (event) => {
        const f = event.target;
        if (f instanceof HTMLFormElement && /\/cart\/add/.test(f.action || '')) {
          setTimeout(() => this.#cargarCarrito(), 1500);
          setTimeout(() => this.#cargarCarrito(), 4000);
        }
      },
      { signal, capture: true }
    );
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** Gramos (o ml) de este producto que ya están en el carrito, sumando todas sus presentaciones. */
  #enCarrito = 0;

  async #cargarCarrito() {
    const productId = this.closest('[data-product-id]')?.dataset.productId;
    if (!productId) return;
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      let total = 0;
      for (const item of cart.items || []) {
        if (String(item.product_id) !== String(productId)) continue;
        const base = parseBase(item.variant_title || item.title || '', item.options_with_values);
        total += base * (item.quantity || 0);
      }
      this.#enCarrito = total;
      this.#render();
    } catch (e) {
      /* sin carrito legible: se muestra solo la selección */
    }
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#render();
  }

  /** @param {number} [quantity] */
  #render(quantity) {
    let qty = quantity;
    if (!qty || Number.isNaN(qty)) {
      const scope = this.closest('product-form-component') || this.closest('.product-details') || document;
      const input = /** @type {HTMLInputElement | null} */ (scope.querySelector('input[name="quantity"]'));
      qty = input ? parseInt(input.value, 10) : 1;
    }
    if (!Number.isInteger(qty) || qty < 1) qty = 1;

    const base = parseInt(this.dataset.base || '0', 10);
    const unit = this.dataset.unidad === 'ml' ? 'ml' : 'g';
    const price = parseInt(this.dataset.precio || '0', 10);
    const format = this.dataset.formato || '${{amount}}';
    if (!base || !price) return;

    const weightEl = this.querySelector('[data-casa-llevas-peso]');
    const totalEl = this.querySelector('[data-casa-llevas-total]');
    if (weightEl) weightEl.textContent = formatQuantity(base * qty, unit);
    if (totalEl) totalEl.textContent = formatMoney(price * qty, format);

    const carritoEl = this.querySelector('[data-casa-llevas-carrito]');
    if (carritoEl) {
      if (this.#enCarrito > 0) {
        const ya = formatQuantity(this.#enCarrito, unit);
        const conEsto = formatQuantity(this.#enCarrito + base * qty, unit);
        carritoEl.textContent = `Ya tienes ${ya} en tu carrito · con esto llevarías ${conEsto} de este producto`;
        carritoEl.hidden = false;
      } else {
        carritoEl.textContent = '';
        carritoEl.hidden = true;
      }
    }
  }
}

if (!customElements.get('casa-llevas')) {
  customElements.define('casa-llevas', CasaLlevas);
}

class CasaActualiza extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    if (!promise) return;

    promise
      .then((result) => {
        const detail = result?.detail;
        const html = detail?.html;
        if (!html || typeof html.querySelector !== 'function') return;
        if (!detail.newProduct && detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        const fresh = html.querySelector(`casa-actualiza[data-casa-id="${this.dataset.casaId}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

if (!customElements.get('casa-actualiza')) {
  customElements.define('casa-actualiza', CasaActualiza);
}


/**
 * Lee la presentación de un renglón del carrito ("500 gr", "1Kg", "250 ml", "1 L") y la
 * devuelve en la unidad base (g o ml), en enteros. Sin dato → 0.
 * @param {string} texto
 * @param {Array<{name:string,value:string}>} [opciones]
 */
function parseBase(texto, opciones) {
  const candidatos = [texto, ...(opciones || []).map((o) => o && o.value)].filter(Boolean);
  for (const c of candidatos) {
    const m = String(c).match(/(\d+(?:[.,]\d+)?)\s*(kg|kilos?|g|grs?|gramos|ml|lts?|l|litros?)\b/i);
    if (!m) continue;
    const n = parseFloat(m[1].replace(',', '.'));
    const u = m[2].toLowerCase();
    if (u.startsWith('k')) return Math.round(n * 1000);
    if (u === 'l' || u.startsWith('lt') || u.startsWith('litro')) return Math.round(n * 1000);
    return Math.round(n);
  }
  return 0;
}

/* Pills de presentación: la clase .casa-pill--activa sigue al radio marcado (no depende de :has) */
function casaSincronizarPills(root = document) {
  root.querySelectorAll('fieldset.variant-option--casa-pills').forEach((fs) => {
    fs.querySelectorAll('input[type="radio"]').forEach((input) => {
      const label = fs.querySelector(`label[for="${input.id}"]`) || input.closest('label');
      if (label) label.classList.toggle('casa-pill--activa', input.checked);
    });
  });
}
document.addEventListener('change', (e) => {
  if (e.target instanceof HTMLInputElement && e.target.closest('fieldset.variant-option--casa-pills')) casaSincronizarPills();
});
document.addEventListener('DOMContentLoaded', () => casaSincronizarPills());
casaSincronizarPills();
new MutationObserver(() => casaSincronizarPills()).observe(document.documentElement, { subtree: true, childList: true, attributeFilter: ['checked'], attributes: true });
